import { Route } from 'vue-router'
import md5 from 'crypto-js/md5'
import { Vue } from 'nuxt-property-decorator'
import {
  GTM_PAGEVIEW_DELAY,
  GTM_PAGEVIEW_EVENT_NAME,
  GtmChannelFlag,
  GtmEventNames,
  GtmEventTypes,
  GtmPlugin,
  WebKCGtmEventActions,
  WebKCGtmEventCategories,
  WebKCGtmEventLabels,
} from '.'
import { PageLoad } from '~/plugins/pageLoad'
import { Resize } from '~/plugins/resize'
import { ShopConfig } from '~/module/shopConfig/shopConfigPlugin'

enum DeviceCategory {
  desktop = 'd',
  mobile = 'm',
  tablet = 't',
}

const BREADCRUMB_KEY_PREFIX = 'channelCategory'
const SEND_INIT_EVENT_TIMEOUT = 15000

export interface Traffic {
  breadcrumbs: string[]
  channelFlag: GtmChannelFlag
  channelCategoryCode?: string
  channelDivisionCode?: string
  channelCategory1?: string
  channelCategory2?: string
  channelCategory3?: string
  searchResultsNumber?: number
  searchResultsWarningMsg?: string
  feVersion?: string
}

interface Options {
  isInitial?: boolean
}

export interface PageViewEvent extends Event {
  detail: {
    traffic: Traffic
    to: Route
    sendChangeQuery?: string | null
  }
}

export const createPageViewEvent = (
  to: Route,
  traffic: Traffic,
  sendChangeQuery: string | null
): PageViewEvent =>
  new CustomEvent(GTM_PAGEVIEW_EVENT_NAME, {
    detail: {
      to,
      traffic,
      sendChangeQuery,
    },
  })

export interface PageViewLogger {
  (to: Route, traffic: Traffic, options?: Options): void
}

let initZeroEvent: object[] = []

const resolveDeviceCategory = (device: Resize) => {
  if (device.isMobile) {
    return DeviceCategory.mobile
  }

  if (device.isTablet) {
    return DeviceCategory.tablet
  }

  return DeviceCategory.desktop
}

export const createPageViewListener = (
  shopConfig: ShopConfig,
  resize: Resize,
  gtm: GtmPlugin,
  pageLoad: PageLoad
): PageViewLogger => (to: Route, traffic: Traffic, options?: Options): void => {
  pageLoad.finish()

  const user = shopConfig.user

  const { breadcrumbs = [], ...trafficObject } = traffic || {}
  const { shopUrl } = shopConfig
  const isLogged = !!user?.id ?? false
  const isInternal = user?.isInternal ?? false
  const trafficBreadcrumbs = breadcrumbs.reduce(
    (acc, value, index) => ({
      [`${BREADCRUMB_KEY_PREFIX}${index + 1}`]: value,
      ...acc,
    }),
    {} as Record<string, string>
  )

  const zeroEvent = {
    ...(isLogged &&
      user?.id && {
        customerId: user.gtmCustomerId,
        hashedEmail: user?.email ? md5(user?.email).toString() : '',
      }),
    deviceCategory: resolveDeviceCategory(resize),
    internalTraffic: isInternal,
    loggedIn: isLogged,
    SPA_load: true,
    event: GtmEventNames.ZERO_EVENT,
    shopId: window.location.hostname,
    traffic: {
      ...trafficObject,
      ...trafficBreadcrumbs,
    },
  }

  const pageViewEventInitial = {
    documentLocation: shopUrl + to.fullPath,
    documentTitle: document.title || '',
    event: GtmEventNames.PAGE_VIEW,
  }

  const sendEventInit = () => {
    initZeroEvent.forEach((initZeroEven) => {
      gtm.push(initZeroEven)
    })

    initZeroEvent = []
  }

  const sendEventInitTimeout = () => {
    setTimeout(() => {
      //  sendEventInit()
    }, SEND_INIT_EVENT_TIMEOUT)
  }

  if (options?.isInitial) {
    initZeroEvent[0] = zeroEvent
    initZeroEvent[1] = pageViewEventInitial
    sendEventInitTimeout()

    const submitEvent = () => {
      const pageLoadTime = pageLoad.pageLoadTime()

      initZeroEvent[1] = {
        ...initZeroEvent[1],
        pageLoadTime,
      }

      sendEventInit()
    }

    if (document.readyState === 'complete') {
      // because firefox and chrome dont load in the same way
      submitEvent()
    } else {
      window.addEventListener('load', function () {
        setTimeout(() => {
          submitEvent()
        }, 0) // keep 0 delay, to call this at the end of the event queue
      })
    }
  } else {
    sendEventInit()

    const pageLoadTime = pageLoad.spaLoadTime()

    setTimeout(() => {
      const pageViewEvent = {
        documentLocation: shopUrl + to.fullPath,
        documentTitle: document.title || '',
        event: GtmEventNames.PAGE_VIEW,
        pageLoadTime,
      }
      gtm.push({
        ...zeroEvent,
      })

      gtm.push(pageViewEvent)
    }, GTM_PAGEVIEW_DELAY)
  }
}

export const trackWebKcEvent = (
  page: Vue,
  eventAction: WebKCGtmEventActions,
  eventLabel: WebKCGtmEventLabels,
  value?: number | undefined,
  eventCategory: WebKCGtmEventCategories = WebKCGtmEventCategories.KC
) => {
  const { $pushAnalyticsEvent } = page

  $pushAnalyticsEvent(GtmEventTypes.EventFire, {
    eventAction: eventAction.toString(),
    eventCategory: eventCategory.toString(),
    eventLabel: eventLabel.toString(),
    eventNonInteractionFlag: true,
    eventValue: value || 1,
  })
}

export const trackWebKcClickEvent = (
  page: Vue,
  eventLabel: WebKCGtmEventLabels
) => {
  trackWebKcEvent(page, WebKCGtmEventActions.BUTTON_CLICKS, eventLabel)
}

export const trackWebKcPageView = (
  page: Vue,
  channelCategory2: string,
  channelCategory3?: string
) => {
  const { $route, $trackPageView } = page

  if ($trackPageView) {
    $trackPageView($route, {
      breadcrumbs: [],
      channelCategoryCode: GtmChannelFlag.OTHER,
      channelCategory1: page.$t('common.gtm_channel_category_name').toString(),
      channelCategory2: page.$t(channelCategory2).toString(),
      channelCategory3: channelCategory3
        ? page.$t(channelCategory3).toString()
        : undefined,
      channelFlag: GtmChannelFlag.OTHER,
      feVersion: 'SPA_V1',
    })
  }
}
